/**
 * 📌 Script: i18nBoot.js
 * 📂 Caminho: src/i18nBoot.js
 * 📝 Descrição: Inicializa o i18n lendo `usersConfig/{uid}.lang`
 *   no Firestore. Aplica fallback "de" e sincroniza mudanças
 *   ao vivo com `onSnapshot`.
 *
 * 🔄 Relação com o Projeto:
 *  - Chamado no boot da app (main.js) antes de `app.mount()`.
 *  - Mantém o idioma consistente entre abas/dispositivos.
 *
 * 📥 Entradas (Parâmetros):
 *  - `i18n` (instância criada via createI18n).
 *
 * 📤 Saídas:
 *  - Promise<void> resolvida quando a primeira leitura concluir.
 *
 * 🧩 Exports:
 *  - `initI18nBoot(i18n)` - inicialização e listener vivo.
 *
 * 🔗 Dependências:
 *  - **Firebase Web SDK v9:** firebase/auth, firebase/firestore.
 *  - **Vue I18n:** i18n.global.locale.value.
 */

import { getAuth, onAuthStateChanged } from "firebase/auth";
import { getFirestore, doc, getDoc, onSnapshot } from "firebase/firestore";

export async function initI18nBoot(i18n) {
  const auth = getAuth();
  const db = getFirestore();

  const apply = (code) => {
    const target = code || "de";
    i18n.global.locale.value = target; // escrever no Ref
    try {
      localStorage.setItem("sps.lang", target);
    } catch (err) {
      void err;
    }
    try {
      document.documentElement.lang = target;
    } catch (err) {
      void err;
    }
  };

  // Evita flash: usa cache local se existir.
  try {
    const cached = localStorage.getItem("sps.lang");
    if (cached) apply(cached);
  } catch (err) {
    // ignore storage errors
  }

  // 🔹 Mapeia navigator.language -> "de" | "pt" (fallback "de")
  const detectFromNavigator = () => {
    try {
      const nav = String(navigator?.language || "de").toLowerCase();
      const avail = i18n.global.availableLocales || [];
      const parts = nav.split("-");
      const candidates = [nav, parts[0]];
      for (const c of candidates) {
        if (avail.includes(c)) return c;
      }
    } catch (err) {
      void err;
    }
    return "de";
  };

  await new Promise((resolve) => {
    let resolved = false;
    let stopUserDoc = null;

    onAuthStateChanged(auth, async (user) => {
      if (!user) {
        // encerra listener vivo do user anterior
        if (typeof stopUserDoc === "function") {
          try {
            stopUserDoc();
          } catch (err) {
            void err;
          }
          stopUserDoc = null;
        }
        try {
          const cached = localStorage.getItem("sps.lang");
          apply(cached || detectFromNavigator());
        } catch (e) {
          apply(detectFromNavigator());
        }
        if (!resolved) {
          resolved = true;
          resolve();
        }
        return;
      }

      const uref = doc(db, "usersConfig", user.uid);

      try {
        const snap = await getDoc(uref);
        const lang = snap.data()?.lang || "de";
        apply(lang);
      } catch (err) {
        console.error("i18nBoot init error:", err);
        apply("de");
      }

      if (typeof stopUserDoc === "function") {
        try {
          stopUserDoc();
        } catch (err) {
          void err;
        }
      }

      stopUserDoc = onSnapshot(
        uref,
        (s) => {
          const lang = s.data()?.lang || "de";
          if (lang && i18n.global.locale.value !== lang) apply(lang);
        },
        (err) => {
          // ignora ao deslogar (rules exigem auth)
          const code = err?.code || "";
          if (code === "permission-denied" && !auth.currentUser) {
            return;
          }
          console.error("i18nBoot listen error:", err);
        }
      );

      if (!resolved) {
        resolved = true;
        resolve();
      }
    });
  });
}
